import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, maybeFunnelPrompt } from "@/lib/api";
import { mergeReferenti } from "@/lib/referenti";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, ClipboardCheck, Users } from "lucide-react";

const ESITI = [
  ["positivo", "Positivo"], ["interlocutorio", "Interlocutorio"], ["da_approfondire", "Da approfondire"],
  ["negativo", "Negativo"], ["nessun_interesse", "Nessun interesse"], ["non_effettuato", "Non effettuato"],
];
const ARGOMENTI = [
  ["prodotti_torri", "Prodotti TORRI"], ["prezzi_condizioni", "Prezzi / Condizioni commerciali"],
  ["progetto_specifico", "Progetto specifico"], ["nuovi_prodotti", "Nuovi prodotti / Tecnologie"],
  ["formazione", "Formazione"], ["chiavi_in_mano", "Impianti chiavi in mano"],
  ["collaborazione", "Collaborazione Partner"], ["assistenza", "Assistenza / Service"],
  ["problemi_aperti", "Problemi aperti"], ["altro", "Altro"],
];
const AZIONI = [
  ["richiamare", "Richiamare"], ["nuova_visita", "Nuova visita"], ["inviare_offerta", "Inviare offerta"],
  ["inviare_materiale", "Inviare materiale"], ["inviare_campione", "Inviare campione"], ["demo_formazione", "Demo / Formazione"],
  ["aprire_opportunita", "Aprire opportunità"], ["attendere_cliente", "Attendere cliente"], ["nessuna_azione", "Nessuna azione"], ["altro", "Altro"],
];
const NESSUNA_MOTIVI = [
  ["consolidato", "Rapporto consolidato / nessuna necessità immediata"], ["nessun_interesse", "Nessun interesse"],
  ["cliente_perso", "Cliente perso"], ["attendere", "Attendere futura esigenza"], ["altro", "Altro"],
];
const SEGMENTI = [["residenziale", "Residenziale"], ["ci", "Commerciale & Industriale"], ["utility", "Utility Scale"]];

const Section = ({ n, title, children, required }) => (
  <div className="rounded-lg border border-border p-4">
    <div className="mb-3 flex items-center gap-2">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{n}</span>
      <h3 className="font-heading text-sm font-bold">{title}{required && <span className="text-destructive"> *</span>}</h3>
    </div>
    {children}
  </div>
);

const EMPTY = {
  target_kind: "cliente", partner_id: "none", prospect_id: "none", date: "", time: "", esito: "", status: "svolto",
  ne_motivo: "", ne_nuova_data: "", ne_nota: "",
  interloc_mode: "new", contact_id: "", nome: "", cognome: "", ruolo: "", telefono: "", email: "",
  argomenti: [], argomenti_altro: "",
  opportunita_presente: null,
  opp: { tipologia: "", prodotto: "", kwp: "", valore: "", probabilita: "", tempistica: "", note: "" },
  verifica: "non_verificato",
  pb: { kwp_installati_anno: "", potenziale_kwp: "", segmenti: [], squadre_installazione: "", installazione_interna: false, progettazione_interna: false, manutenzione_interna: false, province_operative: "" },
  resoconto: "",
  prossima_azione_tipo: "", prossima_azione_responsabile: "none", prossima_azione_data: "",
  nessuna_azione_motivo: "", nessuna_azione_nota: "",
  affiancamento_richiesto: false,
};

export default function VisitaB2BDialog({ open, onOpenChange, partners, prospects = [], managers = [], actor = "responsabile_commerciale", defaultPartnerId, defaultProspectId, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState(null); // {sufficiente, motivo}
  const [contattiList, setContattiList] = useState([]);

  useEffect(() => {
    const kind = f.target_kind;
    const id = kind === "prospect" ? f.prospect_id : f.partner_id;
    if (!open || !id || id === "none") { setContattiList([]); return; }
    let active = true;
    setF((s) => (s.contact_id ? { ...s, contact_id: "" } : s));
    api.get(kind === "prospect" ? `/prospects/${id}` : `/partners/${id}`).then((r) => {
      if (!active) return;
      const raw = mergeReferenti(r.data);
      setContattiList((raw || []).map((c, i) => {
        const full = (c.nome || "").trim() || c.email || "Contatto";
        return { key: String(i), full, nome: c.nome || "", cognome: "", ruolo: c.ruolo || "", telefono: c.telefono || c.tel_cellulare || c.tel_fisso || "", email: c.email || "", principale: !!c.principale };
      }));
    }).catch(() => { if (active) setContattiList([]); });
    return () => { active = false; };
  }, [open, f.target_kind, f.partner_id, f.prospect_id]);

  useEffect(() => {
    if (open) {
      if (defaultProspectId) setF({ ...EMPTY, target_kind: "prospect", prospect_id: defaultProspectId, partner_id: "none" });
      else setF({ ...EMPTY, partner_id: defaultPartnerId || "none" });
      setReport(null); setQuery(""); setShowResults(false);
    }
  }, [open, defaultPartnerId, defaultProspectId]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setOpp = (k, v) => setF((s) => ({ ...s, opp: { ...s.opp, [k]: v } }));
  const setPb = (k, v) => setF((s) => ({ ...s, pb: { ...s.pb, [k]: v } }));
  const toggleArg = (v) => setF((s) => ({ ...s, argomenti: s.argomenti.includes(v) ? s.argomenti.filter((x) => x !== v) : [...s.argomenti, v] }));
  const toggleSeg = (v) => setF((s) => ({ ...s, pb: { ...s.pb, segmenti: s.pb.segmenti.includes(v) ? s.pb.segmenti.filter((x) => x !== v) : [...s.pb.segmenti, v] } }));

  const isProspect = f.target_kind === "prospect";
  const isPlanned = f.status === "pianificato";
  const notEffected = f.esito === "non_effettuato";

  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const allOptions = useMemo(() => [
    ...partners.map((p) => ({ id: p.id, name: p.name, kind: "cliente" })),
    ...prospects.map((p) => ({ id: p.id, name: p.name, kind: "prospect" })),
  ], [partners, prospects]);
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allOptions.filter((o) => (o.name || "").toLowerCase().includes(q)).slice(0, 30);
  }, [query, allOptions]);
  const pickRecord = (o) => {
    if (o.kind === "prospect") setF((s) => ({ ...s, target_kind: "prospect", prospect_id: o.id, partner_id: "none" }));
    else setF((s) => ({ ...s, target_kind: "cliente", partner_id: o.id, prospect_id: "none" }));
    setQuery(o.name);
    setShowResults(false);
  };

  const checkReport = async () => {
    setChecking(true);
    try {
      const r = await api.post("/activities/check-note", { text: f.resoconto });
      setReport(r.data);
      if (r.data.enabled === false || r.data.mode === "avvisa") return true;
      return r.data.sufficiente;
    } catch { setReport({ sufficiente: true, motivo: "Controllo non disponibile" }); return true; }
    finally { setChecking(false); }
  };

  const buildPayload = () => {
    const p = {
      partner_id: isProspect ? null : (f.partner_id === "none" ? null : f.partner_id),
      prospect_id: isProspect ? (f.prospect_id === "none" ? null : f.prospect_id) : null,
      date: f.date, time: f.time, actor,
      owner_id: isAdmin ? null : undefined, esito: f.esito,
    };
    if (notEffected) {
      p.non_effettuato = { motivo: f.ne_motivo, nuova_data: f.ne_nuova_data || null, nota: f.ne_nota };
      return p;
    }
    if (f.interloc_mode === "existing") {
      const c = contattiList.find((x) => x.key === f.contact_id);
      p.interlocutore = c ? { nome: c.nome, cognome: c.cognome, ruolo: c.ruolo, telefono: c.telefono, email: c.email } : {};
      p.save_contact = false;
    } else {
      p.interlocutore = { nome: f.nome, cognome: f.cognome, ruolo: f.ruolo, telefono: f.telefono, email: f.email };
      p.save_contact = true;
    }
    p.argomenti = f.argomenti;
    p.argomenti_altro = f.argomenti_altro;
    p.opportunita_presente = f.opportunita_presente;
    p.verifica_profilazione = f.verifica;
    if (f.verifica === "no") {
      const pb = {};
      if (f.pb.kwp_installati_anno) pb.kwp_installati_anno = Number(f.pb.kwp_installati_anno);
      if (f.pb.segmenti.length) pb.segmenti = f.pb.segmenti;
      if (f.pb.squadre_installazione) pb.squadre_installazione = Number(f.pb.squadre_installazione);
      pb.installazione_interna = f.pb.installazione_interna;
      pb.progettazione_interna = f.pb.progettazione_interna;
      pb.manutenzione_interna = f.pb.manutenzione_interna;
      if (f.pb.province_operative) pb.province_operative = f.pb.province_operative.split(",").map((x) => x.trim()).filter(Boolean);
      p.profilo_b2b_update = pb;
      if (f.pb.potenziale_kwp) p.potenziale_torri_kwp = Number(f.pb.potenziale_kwp);
    }
    p.resoconto = f.resoconto;
    p.prossima_azione_tipo = f.prossima_azione_tipo;
    p.prossima_azione_responsabile = f.prossima_azione_responsabile === "none" ? null : f.prossima_azione_responsabile;
    p.prossima_azione_data = f.prossima_azione_data || null;
    p.nessuna_azione_motivo = f.nessuna_azione_motivo;
    p.nessuna_azione_nota = f.nessuna_azione_nota;
    p.affiancamento_richiesto = !!f.affiancamento_richiesto;
    return p;
  };

  const save = async () => {
    // Modalità PIANIFICATA: fissa un appuntamento futuro senza esito/resoconto (si compilano dopo la visita).
    if (isPlanned) {
      const targetOk = isProspect ? f.prospect_id !== "none" : f.partner_id !== "none";
      if (!targetOk || !f.date) { toast.error("Seleziona Cliente/Prospect e data dell'appuntamento"); return; }
      setSaving(true);
      try {
        const r = await api.post("/activities", {
          type: "visita_partner", status: "pianificato",
          partner_id: isProspect ? null : (f.partner_id === "none" ? null : f.partner_id),
          prospect_id: isProspect ? (f.prospect_id === "none" ? null : f.prospect_id) : null,
          date: f.date, time: f.time, actor, notes: f.resoconto || "",
          affiancamento_richiesto: !!f.affiancamento_richiesto,
        });
        toast.success("Appuntamento pianificato in agenda");
        onOpenChange(false); onSaved?.();
        maybeFunnelPrompt(r);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setSaving(false); }
      return;
    }
    const targetOk = isProspect ? f.prospect_id !== "none" : f.partner_id !== "none";
    if (!targetOk || !f.date || !f.esito) { toast.error("Seleziona Cliente/Prospect, data ed esito"); return; }
    if (!notEffected && f.interloc_mode === "existing" && !f.contact_id) { toast.error("Seleziona un referente esistente"); return; }
    if (!notEffected) {
      // Blocco salvataggio finché il resoconto non è sufficiente
      const ok = report?.sufficiente ? true : await checkReport();
      if (!ok) { toast.error("Resoconto poco informativo: integralo prima di salvare"); return; }
    }
    setSaving(true);
    try {
      const r = await api.post("/activities/visita-b2b", buildPayload());
      toast.success(r.data.message);
      onOpenChange(false);
      onSaved?.();
      maybeFunnelPrompt(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="visita-b2b-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" /> Registra Visita</DialogTitle>
          <DialogDescription>{isPlanned ? "Fissa un appuntamento futuro: bastano cliente/prospect, data e ora. Il resoconto si compila dopo la visita." : "Ogni appuntamento produce informazioni strutturate: esito, interlocutore, argomenti, opportunità, resoconto e prossima azione."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="vb-status-toggle">
            <span className="text-sm font-semibold">Tipo:</span>
            <Button type="button" size="sm" variant={!isPlanned ? "default" : "outline"} onClick={() => set("status", "svolto")} data-testid="vb-status-svolta">Visita svolta</Button>
            <Button type="button" size="sm" variant={isPlanned ? "default" : "outline"} onClick={() => set("status", "pianificato")} data-testid="vb-status-pianificata">Pianifica appuntamento</Button>
            <span className="text-[11px] text-muted-foreground">{isPlanned ? "Il resoconto si compila dopo, quando segni la visita come svolta." : "Registra una visita già effettuata (esito e resoconto obbligatori)."}</span>
          </div>
          {actor === "agenzia" && !notEffected && (
            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border-2 border-orange-300 bg-orange-50 p-3" data-testid="vb-affiancamento-row">
              <Checkbox checked={f.affiancamento_richiesto} onCheckedChange={(v) => set("affiancamento_richiesto", !!v)} className="border-orange-400 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600" data-testid="vb-affiancamento-checkbox" />
              <Users className="h-4 w-4 shrink-0 text-orange-600" />
              <span className="text-sm text-orange-900"><strong>Responsabile in affiancamento</strong> <span className="text-orange-800/80">— l'appuntamento viene aggiunto in automatico all'agenda del Responsabile, che poi inserirà la nota.</span></span>
            </label>
          )}
          {/* 1. Esito & Partner */}
          <Section n={1} title={isPlanned ? "Appuntamento & Partner" : "Esito appuntamento & Partner"} required>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Cerca cliente o prospect</Label>
                <div className="relative">
                  <Input
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
                    onFocus={() => query && setShowResults(true)}
                    onBlur={() => setTimeout(() => setShowResults(false), 150)}
                    placeholder="Digita il nome — cerca su clienti e prospect"
                    data-testid="vb-search" />
                  {showResults && searchResults.length > 0 && (
                    <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="vb-search-results">
                      {searchResults.map((o) => (
                        <button key={`${o.kind}-${o.id}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickRecord(o)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                          data-testid={`vb-search-opt-${o.id}`}>
                          <span className="truncate">{o.name}</span>
                          <Badge className={o.kind === "prospect" ? "shrink-0 border-amber-300 bg-amber-100 text-[10px] text-amber-800" : "shrink-0 border-emerald-300 bg-emerald-100 text-[10px] text-emerald-800"}>
                            {o.kind === "prospect" ? "Prospect" : "Cliente"}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  )}
                  {showResults && query.trim() && searchResults.length === 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover px-3 py-2 text-sm text-muted-foreground shadow-lg" data-testid="vb-search-empty">Nessun risultato</div>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">Selezionando un risultato, il Tipo anagrafica (Cliente/Prospect) viene impostato automaticamente.</p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Tipo anagrafica</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={f.target_kind === "cliente" ? "default" : "outline"} onClick={() => set("target_kind", "cliente")} data-testid="vb-kind-cliente">Cliente</Button>
                  <Button type="button" size="sm" variant={f.target_kind === "prospect" ? "default" : "outline"} onClick={() => set("target_kind", "prospect")} data-testid="vb-kind-prospect">Prospect</Button>
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">{isProspect ? "Prospect" : "Cliente"}</Label>
                {isProspect ? (
                  <Select value={f.prospect_id} onValueChange={(v) => set("prospect_id", v)}>
                    <SelectTrigger data-testid="vb-prospect"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{prospects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Select value={f.partner_id} onValueChange={(v) => set("partner_id", v)}>
                    <SelectTrigger data-testid="vb-partner"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Data</Label><DatePicker value={f.date} onChange={(v) => set("date", v)} data-testid="vb-date" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Orario</Label><Input type="time" value={f.time} onChange={(e) => set("time", e.target.value)} data-testid="vb-time" /></div>
              {!isPlanned && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Esito</Label>
                <Select value={f.esito} onValueChange={(v) => set("esito", v)}>
                  <SelectTrigger data-testid="vb-esito"><SelectValue placeholder="Seleziona esito" /></SelectTrigger>
                  <SelectContent>{ESITI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              )}
              {isPlanned && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Note / obiettivo dell'appuntamento (facoltativo)</Label>
                <Textarea rows={2} value={f.resoconto} onChange={(e) => set("resoconto", e.target.value)} placeholder="Es. presentare la gamma TORRI, obiettivo dell'incontro…" data-testid="vb-plan-note" />
              </div>
              )}
            </div>
            {!isPlanned && notEffected && (
              <div className="mt-3 grid grid-cols-1 gap-3 rounded-md bg-accent p-3 sm:grid-cols-2" data-testid="vb-non-effettuato">
                <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Motivo *</Label><Input value={f.ne_motivo} onChange={(e) => set("ne_motivo", e.target.value)} data-testid="vb-ne-motivo" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Nuova data</Label><DatePicker value={f.ne_nuova_data} onChange={(v) => set("ne_nuova_data", v)} placeholder="Nuova data" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Nota</Label><Input value={f.ne_nota} onChange={(e) => set("ne_nota", e.target.value)} /></div>
              </div>
            )}
          </Section>

          {!isPlanned && !notEffected && (
            <>
              {/* 2. Interlocutore */}
              <Section n={2} title="Chi hai incontrato?" required>
                <div className="mb-2 flex gap-2">
                  <Button type="button" size="sm" variant={f.interloc_mode === "existing" ? "default" : "outline"} onClick={() => set("interloc_mode", "existing")} data-testid="vb-int-existing">Contatto esistente</Button>
                  <Button type="button" size="sm" variant={f.interloc_mode === "new" ? "default" : "outline"} onClick={() => set("interloc_mode", "new")} data-testid="vb-int-new">Nuovo contatto</Button>
                </div>
                {f.interloc_mode === "existing" ? (
                  <Select value={f.contact_id} onValueChange={(v) => set("contact_id", v)}>
                    <SelectTrigger data-testid="vb-contact"><SelectValue placeholder={contattiList.length ? "Seleziona referente" : "Nessun referente in anagrafica"} /></SelectTrigger>
                    <SelectContent>{contattiList.map((c) => <SelectItem key={c.key} value={c.key}>{c.full}{c.ruolo ? ` · ${c.ruolo}` : ""}{c.principale ? " · principale" : ""}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5"><Label className="text-xs">Nome</Label><Input value={f.nome} onChange={(e) => set("nome", e.target.value)} data-testid="vb-nome" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Cognome</Label><Input value={f.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="vb-cognome" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Ruolo</Label><Input value={f.ruolo} onChange={(e) => set("ruolo", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Telefono</Label><Input value={f.telefono} onChange={(e) => set("telefono", e.target.value)} /></div>
                    <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
                    <p className="text-[11px] text-muted-foreground sm:col-span-2">Il nuovo contatto verrà salvato nell'anagrafica del Cliente/Partner.</p>
                  </div>
                )}
              </Section>

              {/* 3. Argomenti */}
              <Section n={3} title="Argomenti trattati" required>
                <div className="flex flex-wrap gap-2">
                  {ARGOMENTI.map(([v, l]) => (
                    <button key={v} type="button" onClick={() => toggleArg(v)} data-testid={`vb-arg-${v}`}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${f.argomenti.includes(v) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"}`}>{l}</button>
                  ))}
                </div>
                {f.argomenti.includes("altro") && <Input className="mt-2" placeholder="Specifica altro argomento" value={f.argomenti_altro} onChange={(e) => set("argomenti_altro", e.target.value)} data-testid="vb-arg-altro" />}
              </Section>

              {/* 4. Opportunità */}
              <Section n={4} title="È emersa un'opportunità commerciale concreta?" required>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={f.opportunita_presente === true ? "default" : "outline"} onClick={() => set("opportunita_presente", true)} data-testid="vb-opp-si">Sì</Button>
                  <Button type="button" size="sm" variant={f.opportunita_presente === false ? "default" : "outline"} onClick={() => set("opportunita_presente", false)} data-testid="vb-opp-no">No</Button>
                </div>
              </Section>

              {/* 5. Verifica profilazione */}
              <Section n={5} title="Le informazioni di profilazione del Partner sono ancora corrette?">
                <Select value={f.verifica} onValueChange={(v) => set("verifica", v)}>
                  <SelectTrigger data-testid="vb-verifica"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Sì</SelectItem>
                    <SelectItem value="non_verificato">Non verificato</SelectItem>
                    <SelectItem value="no">No — aggiorna i dati</SelectItem>
                  </SelectContent>
                </Select>
                {f.verifica === "no" && (
                  <div className="mt-3 grid grid-cols-1 gap-3 rounded-md bg-accent p-3 sm:grid-cols-2" data-testid="vb-profilo-fields">
                    <div className="space-y-1.5"><Label className="text-xs">kWp installati/anno</Label><Input type="number" value={f.pb.kwp_installati_anno} onChange={(e) => setPb("kwp_installati_anno", e.target.value)} data-testid="vb-pb-kwp" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Potenziale TORRI (kWp)</Label><Input type="number" value={f.pb.potenziale_kwp} onChange={(e) => setPb("potenziale_kwp", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Numero squadre</Label><Input type="number" value={f.pb.squadre_installazione} onChange={(e) => setPb("squadre_installazione", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Province/Regioni operative</Label><Input value={f.pb.province_operative} onChange={(e) => setPb("province_operative", e.target.value)} placeholder="es. MI, TO, BS" /></div>
                    <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Segmenti serviti</Label>
                      <div className="flex flex-wrap gap-2">{SEGMENTI.map(([v, l]) => (
                        <button key={v} type="button" onClick={() => toggleSeg(v)} className={`rounded-full border px-3 py-1 text-xs ${f.pb.segmenti.includes(v) ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{l}</button>
                      ))}</div></div>
                    <div className="flex gap-4 sm:col-span-2">
                      {[["installazione_interna", "Installazione interna"], ["progettazione_interna", "Progettazione interna"], ["manutenzione_interna", "Manutenzione interna"]].map(([k, l]) => (
                        <label key={k} className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={f.pb[k]} onChange={(e) => setPb(k, e.target.checked)} />{l}</label>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground sm:col-span-2">Al salvataggio il Potential Score e la profilazione verranno ricalcolati.</p>
                  </div>
                )}
              </Section>

              {/* 6. Resoconto + 7. AI */}
              <Section n={6} title="Resoconto dell'appuntamento" required>
                <Textarea rows={4} value={f.resoconto} onChange={(e) => { set("resoconto", e.target.value); setReport(null); }}
                  placeholder="Descrivi sinteticamente cosa è emerso dall'incontro, l'interesse del cliente, le decisioni prese e le informazioni utili per il prossimo contatto."
                  data-testid="vb-resoconto" />
                <div className="mt-2 flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={checkReport} disabled={checking || !f.resoconto} data-testid="vb-check-report">
                    {checking ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />} Verifica qualità (AI)
                  </Button>
                  {report && (
                    <span className={`flex items-center gap-1 text-xs ${report.sufficiente ? "text-emerald-600" : "text-destructive"}`} data-testid="vb-report-result">
                      {report.sufficiente ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                      {report.sufficiente ? "Resoconto adeguato" : report.motivo}
                    </span>
                  )}
                </div>
              </Section>

              {/* 8/9. Prossima azione */}
              <Section n={7} title="Cosa succede adesso?" required>
                <Select value={f.prossima_azione_tipo} onValueChange={(v) => set("prossima_azione_tipo", v)}>
                  <SelectTrigger data-testid="vb-azione"><SelectValue placeholder="Seleziona prossima azione" /></SelectTrigger>
                  <SelectContent>{AZIONI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                {f.prossima_azione_tipo && f.prossima_azione_tipo !== "nessuna_azione" && (
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="vb-azione-fields">
                    {isAdmin && (
                      <div className="space-y-1.5"><Label className="text-xs">Responsabile</Label>
                        <Select value={f.prossima_azione_responsabile} onValueChange={(v) => set("prossima_azione_responsabile", v)}>
                          <SelectTrigger data-testid="vb-azione-resp"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">—</SelectItem>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                        </Select></div>
                    )}
                    <div className="space-y-1.5"><Label className="text-xs">Data prossima azione *</Label><DatePicker value={f.prossima_azione_data} onChange={(v) => set("prossima_azione_data", v)} data-testid="vb-azione-data" /></div>
                  </div>
                )}
                {f.prossima_azione_tipo === "nessuna_azione" && (
                  <div className="mt-3 space-y-3 rounded-md bg-accent p-3" data-testid="vb-nessuna-fields">
                    <div className="space-y-1.5"><Label className="text-xs">Perché non è prevista una prossima azione? *</Label>
                      <Select value={f.nessuna_azione_motivo} onValueChange={(v) => set("nessuna_azione_motivo", v)}>
                        <SelectTrigger data-testid="vb-nessuna-motivo"><SelectValue placeholder="Seleziona motivo" /></SelectTrigger>
                        <SelectContent>{NESSUNA_MOTIVI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-1.5"><Label className="text-xs">Nota</Label><Input value={f.nessuna_azione_nota} onChange={(e) => set("nessuna_azione_nota", e.target.value)} /></div>
                  </div>
                )}
              </Section>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="vb-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isPlanned ? "Pianifica in agenda" : "Salva visita")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
